import { useState, useEffect } from 'react';
import { useCurrentUser } from '../../context/CurrentUserContext';
import { getDemoEnvMgmtOptions } from '../../utils/demoEnvMgmtApi';
import EntitiesTab from './entities-tab';
import ManageOptions from './manage-options';
import ManageUsers from './manage-users';
import './page.css';

function DemoEnvMgmt() {
  const currentUser = useCurrentUser();
  // 'entities' shows the Entities tab; 'admin' shows the admin-only tabs.
  const [view, setView] = useState('entities');
  const [adminFeature, setAdminFeature] = useState('manage-options');

  // Predefined options fetched from the backend (authorized-users, accounts, projects,
  // datasources, users) — everything selectable on this page comes from this list, there's no
  // free-text escape hatch for ids/email anymore (see manage-options.js to add new predefined
  // values).
  const [options, setOptions] = useState({ authorizedUsers: [], accounts: [], projects: [], datasources: [], users: [] });
  const [optionsLoading, setOptionsLoading] = useState(true);
  const [optionsError, setOptionsError] = useState(null);

  const loadOptions = () => {
    setOptionsLoading(true);
    setOptionsError(null);
    return getDemoEnvMgmtOptions()
      .then(setOptions)
      .catch((err) => setOptionsError(err.message))
      .finally(() => setOptionsLoading(false));
  };

  useEffect(() => {
    loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // "Manage options" and "Manage users" are restricted to admin users — derived from the
  // authorized-users list once options have loaded.
  const isAdmin = options.authorizedUsers.some(
    (u) => u.email === currentUser?.email && u.isAdmin,
  );

  // If admin access is lost (options reloaded, permissions changed), fall back to entities view.
  useEffect(() => {
    if (!isAdmin && view === 'admin') {
      setView('entities');
    }
  }, [isAdmin, view]);

  const adminTabs = [
    { key: 'manage-options', label: 'Manage options' },
    { key: 'manage-users', label: 'Manage users' },
  ];

  if (view === 'admin') {
    return (
      <div className="application-container">
        <div className="demo-env-mgmt-page">
          <div className="demo-env-mgmt-title-row">
            <h1 className="demo-env-mgmt-title">CSQ XP Demo Environment Management — Admin</h1>
            <span className="demo-env-mgmt-badge demo-env-mgmt-badge-draft" title="This page is under active development — features may be incomplete or change without notice.">
              Draft
            </span>
          </div>
          <button
            type="button"
            className="demo-env-mgmt-nav-btn"
            onClick={() => setView('entities')}
          >
            ← Back to Entities
          </button>

          <div className="demo-env-mgmt-tabs">
            {adminTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`demo-env-mgmt-tab${adminFeature === tab.key ? ' active' : ''}`}
                onClick={() => setAdminFeature(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {optionsError && <p className="demo-env-mgmt-error">Failed to load predefined options: {optionsError}</p>}

          <div hidden={adminFeature !== 'manage-users'}>
            <ManageUsers
              options={options}
              optionsLoading={optionsLoading}
              onOptionsChanged={loadOptions}
              currentUserEmail={currentUser?.email}
            />
          </div>

          <div hidden={adminFeature !== 'manage-options'}>
            <ManageOptions
              options={options}
              optionsLoading={optionsLoading}
              onOptionsChanged={loadOptions}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="application-container">
      <div className="demo-env-mgmt-page">
        <div className="demo-env-mgmt-title-row">
          <h1 className="demo-env-mgmt-title">CSQ XP Demo Environment Management</h1>
          <span className="demo-env-mgmt-badge demo-env-mgmt-badge-draft" title="This page is under active development — features may be incomplete or change without notice.">
            Draft
          </span>
        </div>
        {isAdmin && (
          <button
            type="button"
            className="demo-env-mgmt-nav-btn"
            onClick={() => setView('admin')}
          >
            Admin →
          </button>
        )}

        <p className="demo-env-mgmt-subtitle">
          Create, list, and delete ContentSquare entities (mappings, zonings, segments,
          properties, charts, dashboards).
        </p>

        {optionsError && <p className="demo-env-mgmt-error">Failed to load predefined options: {optionsError}</p>}

        <EntitiesTab options={options} optionsLoading={optionsLoading} />
      </div>
    </div>
  );
}

export default DemoEnvMgmt;
